import { Document, Packer, Paragraph, TextRun, AlignmentType } from 'docx'
import { jsPDF } from 'jspdf'
import type { DispatchBatchQuery } from '../../api/workspace.api'

// ---- Letter draft shape -----------------------------------------------------
// One draft = one encounter's letter page. Merged renders stack drafts with
// page breaks (PDF: addPage / DOCX: pageBreakBefore).

export interface LetterDraft {
  /** Encounter id this letter page belongs to. Used by the renderers to
   *  look up the chart-scoped "Additional notes" block via
   *  notesByEncounter[encounterId] so each PDF/DOCX page can carry its
   *  own coder note independent of every other chart in the batch. */
  encounterId: string
  claimId: string
  dos: string
  dosDisplay: string
  facility: string
  patientName: string
  accountNo: string
  surgeon: string
  queryBlocks: QueryBlock[]
  queryText: string
}

export interface QueryBlock {
  title: string
  body: string
  chartAnchorText?: string
}

const safe = (v: unknown): string => (v == null ? '' : String(v))

export function emptyDraft(): LetterDraft {
  return {
    encounterId: '',
    claimId: '', dos: '', dosDisplay: '',
    facility: '', patientName: '', accountNo: '',
    surgeon: '', queryBlocks: [], queryText: '',
  }
}

function fmtDateLong(iso: string | null | undefined): string {
  if (!iso) return ''
  // Server-masked PHI (e.g. "**/**/2026") — `new Date()` leniently coerces it
  // to Jan 1 of the year, which would print "Jan 01, 2026" on every masked
  // letter and packet. Pass the mask through verbatim so the letter's Date of
  // Service field reads "**/**/2026" like every other emit site.
  if (iso.includes('*')) return iso
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' })
}

function chartAnchorText(anchor: unknown): string | undefined {
  if (!anchor) return undefined
  if (typeof anchor === 'string') {
    const text = anchor.trim()
    return text || undefined
  }
  if (typeof anchor !== 'object') return undefined
  const text = (anchor as { text?: unknown }).text
  return typeof text === 'string' && text.trim() ? text.trim() : undefined
}

// Builds per-encounter letter drafts directly from the batch query rows. The
// dispatch-batch endpoint joins facility / provider / patient so each query
// row already carries the letter fields. Queries on the same encounter are
// concatenated into one queryText block, separated by blank lines and
// prefixed with the query title so the provider can address each one.
export function buildDraftsFromBatch(queries: DispatchBatchQuery[]): LetterDraft[] {
  const grouped = new Map<string, DispatchBatchQuery[]>()
  for (const q of queries) {
    const list = grouped.get(q.encounter_id) ?? []
    list.push(q)
    grouped.set(q.encounter_id, list)
  }
  const drafts: LetterDraft[] = []
  for (const [encounterId, qs] of grouped) {
    const first = qs[0]
    const queryBlocks: QueryBlock[] = qs.map((q) => ({
      title: (q.title ?? '').trim(),
      body: (q.body ?? '').trim(),
      chartAnchorText: chartAnchorText(q.chart_anchor),
    }))
    const queryText = queryBlocks.map((q) => {
      const title = (q.title ?? '').trim()
      const body = (q.body ?? '').trim()
      return body ? `${title}\n${body}` : title
    }).join('\n\n')
    drafts.push({
      encounterId,
      claimId: first?.claim_id ?? encounterId.slice(0, 8),
      dos: first?.date_of_service ?? '',
      dosDisplay: fmtDateLong(first?.date_of_service),
      facility: first?.facility_name ?? '',
      patientName: first?.patient_name ?? '',
      accountNo: first?.account_no ?? first?.claim_id ?? '',
      surgeon: first?.surgeon_name ?? '',
      queryBlocks,
      queryText,
    })
  }
  return drafts
}

// ---- DOCX renderer ----------------------------------------------------------

const DOCX_PAGE_PROPS = {
  page: {
    size: { width: 12240, height: 15840 },
    margin: { top: 1440, right: 1440, bottom: 1440, left: 1440 },
  },
}

// Per-encounter "Additional notes" block. Empty / whitespace-only notes
// produce zero paragraphs so the block disappears entirely from that page.
function chartNoteDocxBlock(noteForChart: string | undefined): Paragraph[] {
  const n = (noteForChart ?? '').trim()
  if (!n) return []
  const sp = (after = 80) => ({ after })
  const lines = n.split(/\r?\n/)
  return [
    new Paragraph({
      spacing: sp(60),
      children: [new TextRun({ text: 'Additional notes:', bold: true })],
    }),
    ...lines.map((line) => new Paragraph({
      spacing: sp(40),
      children: [new TextRun({ text: line, italics: true })],
    })),
    new Paragraph({ spacing: sp(120) }),
  ]
}

function queryBlocksDocx(blocks: QueryBlock[], fallbackText: string): Paragraph[] {
  const sp = (after = 80) => ({ after })
  if (blocks.length === 0) {
    return [new Paragraph({ spacing: sp(160), children: [new TextRun({ text: safe(fallbackText) })] })]
  }

  return blocks.flatMap((block, idx) => {
    const out: Paragraph[] = []
    if (idx > 0) out.push(new Paragraph({ spacing: sp(80) }))
    if (block.title) {
      out.push(new Paragraph({
        spacing: sp(50),
        children: [new TextRun({ text: block.title, bold: true })],
      }))
    }
    if (block.body) {
      out.push(new Paragraph({
        spacing: sp(80),
        children: [new TextRun({ text: block.body })],
      }))
    }
    if (block.chartAnchorText) {
      out.push(
        new Paragraph({
          spacing: sp(40),
          children: [new TextRun({ text: 'Referenced Chart Text:', bold: true })],
        }),
        ...block.chartAnchorText.split(/\r?\n/).map((line) => new Paragraph({
          spacing: sp(30),
          children: [new TextRun({ text: line, italics: true })],
        })),
      )
    }
    return out
  })
}

function docxChildrenForDraft(
  draft: LetterDraft,
  senderName: string,
  pageBreakBefore: boolean,
  notesByEncounter?: Record<string, string>,
) {
  const noteForChart = notesByEncounter?.[draft.encounterId]
  const sp = (after = 80) => ({ after })
  const field = (lbl: string, val: string) =>
    new Paragraph({
      spacing: sp(),
      children: [
        new TextRun({ text: `${lbl} ` }),
        new TextRun({ text: safe(val), underline: {} }),
      ],
    })
  return [
    ...(pageBreakBefore ? [new Paragraph({ pageBreakBefore: true, children: [] })] : []),
    field('Facility:', draft.facility),
    field('Patient Name:', draft.patientName),
    field('Record/Account Number:', draft.accountNo),
    field('Date of Service:', draft.dosDisplay || draft.dos),
    field('Surgeon:', draft.surgeon),
    new Paragraph({ spacing: sp(200) }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: sp(100),
      children: [new TextRun({ text: 'PHYSICIAN QUERY', bold: true, size: 28 })],
    }),
    new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: sp(200),
      children: [new TextRun({
        text: "In conjunction with a review of the patient's record, please clarify the following:",
        italics: true,
      })],
    }),
    // Render each query separately so its chart anchor can appear directly
    // under the matching query as a quoted "Chart excerpt" block.
    ...queryBlocksDocx(draft.queryBlocks, draft.queryText),
    // Per-chart "Additional notes" block — looked up from
    // notesByEncounter[encounterId] so each page carries its own coder
    // note. Silent when the lookup is empty/whitespace, so charts with
    // no extra notes render exactly like before.
    ...chartNoteDocxBlock(noteForChart),
    new Paragraph({ spacing: sp(200) }),
    new Paragraph({
      spacing: sp(300),
      children: [new TextRun({
        text: "This form will become part of the patient's medical record as an addendum to the operative report and is required to complete the coding on this account.",
        italics: true, size: 20,
      })],
    }),
    new Paragraph({ spacing: sp(60), children: [new TextRun({ text: '______________________________________' })] }),
    new Paragraph({ spacing: sp(80), children: [new TextRun({ text: "Physician's Signature", bold: true })] }),
    new Paragraph({
      spacing: sp(200),
      children: [new TextRun({
        text: "*Physician signature is required as the addendum is part of the patient's legal medical record",
        italics: true, size: 18,
      })],
    }),
    new Paragraph({ spacing: sp(200), children: [new TextRun({ text: 'Date: _______________________________' })] }),
    new Paragraph({ spacing: sp(80), children: [new TextRun({ text: 'Thank You,' })] }),
    new Paragraph({ children: [new TextRun({ text: safe(senderName), italics: true })] }),
  ]
}

export async function buildMergedLetterDocxBlob(
  drafts: LetterDraft[],
  senderName: string,
  notesByEncounter?: Record<string, string>,
): Promise<Blob> {
  const list = drafts.length > 0 ? drafts : [emptyDraft()]
  const children = list.flatMap((d, i) => docxChildrenForDraft(d, senderName, i > 0, notesByEncounter))
  const doc = new Document({
    sections: [{ properties: DOCX_PAGE_PROPS, children }],
  })
  return Packer.toBlob(doc)
}

// ---- PDF renderer ----------------------------------------------------------

function drawChartAnchorBlock(doc: jsPDF, anchorText: string, x: number, y: number, width: number): number {
  const labelH = 5
  const lineH = 5
  const padX = 3
  const padTop = 4
  const padBottom = 5
  const innerW = width - padX * 2
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(10)
  const lines = doc.splitTextToSize(anchorText, innerW)
  const boxH = padTop + labelH + 2 + lines.length * lineH + padBottom

  doc.setFillColor(239, 246, 255)
  doc.setDrawColor(37, 99, 235)
  doc.setLineWidth(0.3)
  doc.rect(x, y, width, boxH, 'FD')

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(9)
  doc.setTextColor(30, 58, 138)
  doc.text('REFERENCED CHART TEXT', x + padX, y + padTop + 2)

  doc.setFont('helvetica', 'italic')
  doc.setFontSize(10)
  doc.setTextColor(51, 65, 85)
  doc.text(lines, x + padX, y + padTop + labelH + 4)

  doc.setTextColor(0, 0, 0)
  doc.setLineWidth(0.2)
  doc.setDrawColor(0, 0, 0)
  return y + boxH + 6
}

function appendLetterToPdf(
  doc: jsPDF,
  draft: LetterDraft,
  senderName: string,
  notesByEncounter?: Record<string, string>,
): void {
  const noteForChart = notesByEncounter?.[draft.encounterId]
  const m = 20
  const pw = 170
  let y = 22
  doc.setFontSize(11)
  doc.setFont('helvetica', 'normal')

  const fld = (lbl: string, val: string) => {
    const label = safe(lbl)
    const value = safe(val)
    doc.text(label, m, y)
    if (value) doc.text(value, m + doc.getTextWidth(label) + 3, y)
    doc.line(m + doc.getTextWidth(label) + 1, y + 1, m + pw, y + 1)
    y += 8
  }

  fld('Facility:', draft.facility)
  fld('Patient Name:', draft.patientName)
  fld('Record/Account Number:', draft.accountNo)
  fld('Date of Service:', draft.dosDisplay || draft.dos)
  fld('Surgeon:', draft.surgeon)
  y += 5

  doc.setFont('helvetica', 'bold')
  doc.setFontSize(14)
  doc.text('PHYSICIAN QUERY', 105, y, { align: 'center' })
  y += 8

  doc.setFont('helvetica', 'italic')
  doc.setFontSize(10)
  const subLines = doc.splitTextToSize(
    "In conjunction with a review of the patient's record, please clarify the following:",
    pw,
  )
  doc.text(subLines, 105, y, { align: 'center' })
  y += subLines.length * 5 + 8

  if (draft.queryBlocks.length > 0) {
    for (const [idx, block] of draft.queryBlocks.entries()) {
      if (idx > 0) y += 4
      if (block.title) {
        doc.setFont('helvetica', 'bold')
        doc.setFontSize(11)
        const titleLines = doc.splitTextToSize(block.title, pw)
        doc.text(titleLines, m, y)
        y += titleLines.length * 6
      }
      if (block.body) {
        doc.setFont('helvetica', 'normal')
        doc.setFontSize(11)
        const bodyLines = doc.splitTextToSize(block.body, pw)
        doc.text(bodyLines, m, y)
        y += bodyLines.length * 6
      }
      if (block.chartAnchorText) {
        y += 2
        y = drawChartAnchorBlock(doc, block.chartAnchorText, m, y, pw)
      } else {
        y += 4
      }
    }
  } else {
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(11)
    const qLines = doc.splitTextToSize(safe(draft.queryText), pw)
    doc.text(qLines, m, y)
    y += qLines.length * 6 + 5
  }

  // Per-chart "Additional notes" — looked up by draft.encounterId so each
  // page in the merged PDF can carry its own coder note independently.
  // Rendered as a soft-tinted block with a label so the provider can
  // distinguish it from the structured query body. Silent (nothing drawn)
  // when the lookup is empty / whitespace.
  const note = (noteForChart ?? '').trim()
  if (note) {
    const labelH = 5
    const lineH = 5
    const padX = 3
    const padTop = 4
    const padBottom = 5
    const innerW = pw - padX * 2
    doc.setFont('helvetica', 'normal')
    doc.setFontSize(10)
    const noteLines = doc.splitTextToSize(note, innerW)
    const boxH = padTop + labelH + 2 + noteLines.length * lineH + padBottom
    // Soft slate-100 background; subtle slate-300 hairline border.
    doc.setFillColor(241, 245, 249)
    doc.setDrawColor(203, 213, 225)
    doc.setLineWidth(0.2)
    doc.rect(m, y, pw, boxH, 'FD')
    // Label
    doc.setFont('helvetica', 'bold')
    doc.setFontSize(9)
    doc.text('ADDITIONAL NOTES', m + padX, y + padTop + 2)
    // Body
    doc.setFont('helvetica', 'italic')
    doc.setFontSize(10)
    doc.text(noteLines, m + padX, y + padTop + labelH + 4)
    // Reset draw line + advance cursor
    doc.setLineWidth(0.2)
    doc.setDrawColor(0, 0, 0)
    y += boxH + 6
  }

  y += 6
  doc.setFontSize(9)
  doc.setFont('helvetica', 'italic')
  const fLines = doc.splitTextToSize(
    "This form will become part of the patient's medical record as an addendum to the operative report and is required to complete the coding on this account.",
    pw,
  )
  doc.text(fLines, m, y)
  y += fLines.length * 5 + 10

  doc.setFont('helvetica', 'normal')
  doc.setFontSize(11)
  doc.line(m, y, m + 80, y)
  y += 5
  doc.setFont('helvetica', 'bold')
  doc.text("Physician's Signature", m, y)
  y += 6
  doc.setFont('helvetica', 'italic')
  doc.setFontSize(9)
  doc.text(
    "*Physician signature is required as the addendum is part of the patient's legal medical record",
    m, y,
  )
  y += 9
  doc.setFont('helvetica', 'normal')
  doc.setFontSize(11)
  doc.text('Date: _______________________________', m, y)
  y += 12
  doc.text('Thank You,', m, y)
  y += 6
  doc.setFont('helvetica', 'italic')
  doc.text(safe(senderName), m, y)
}

export function buildMergedLetterPdfBlob(
  drafts: LetterDraft[],
  senderName: string,
  notesByEncounter?: Record<string, string>,
): Blob {
  const doc = new jsPDF()
  const list = drafts.length > 0 ? drafts : [emptyDraft()]
  list.forEach((d, i) => {
    if (i > 0) doc.addPage()
    appendLetterToPdf(doc, d, senderName, notesByEncounter)
  })
  return doc.output('blob')
}

// ArrayBuffer variant for in-page PDF viewer (react-pdf needs ArrayBuffer).
export function buildMergedLetterPdfArrayBuffer(
  drafts: LetterDraft[],
  senderName: string,
  notesByEncounter?: Record<string, string>,
): ArrayBuffer {
  const doc = new jsPDF()
  const list = drafts.length > 0 ? drafts : [emptyDraft()]
  list.forEach((d, i) => {
    if (i > 0) doc.addPage()
    appendLetterToPdf(doc, d, senderName, notesByEncounter)
  })
  return doc.output('arraybuffer')
}

// ---- Download helpers ------------------------------------------------------

export async function downloadBlob(blob: Blob, filename: string): Promise<void> {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      const r = reader.result
      if (typeof r !== 'string') {
        reject(new Error('Failed to read blob'))
        return
      }
      resolve(r.includes(',') ? r.split(',')[1] : r)
    }
    reader.onerror = () => reject(new Error('Failed to read blob'))
    reader.readAsDataURL(blob)
  })
}
